import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { camelCase } from 'lodash';
import { BugReportDialogComponent } from './bug-report-dialog.component';
import { EBugReportType, IBugReport, IBugReportDialogData } from './bug-report.interface';

@Injectable({
  providedIn: 'root',
})
export class BugReportService {
  private readonly dialog = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  public openBugReportDialog(type?: EBugReportType, imageId?: string | null): void {
    const data: IBugReportDialogData = { imageId, type };
    const dialogRef = this.dialog.open(BugReportDialogComponent, {
      data,
      disableClose: true,
      maxHeight: '80vh',
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result: IBugReport | undefined) => {
      if (result) {
        this.handleBugReport(result);
      }
    });
  }

  public async getSystemInfo(): Promise<string> {
    if (!('userAgentData' in navigator)) {
      return navigator.userAgent;
    }

    try {
      const uaData = await (
        navigator.userAgentData as {
          getHighEntropyValues: (keys: string[]) => Promise<{
            platform: string;
            architecture: string;
            platformVersion: string;
            fullVersionList: { brand: string; version: string }[];
          }>;
        }
      ).getHighEntropyValues(['platform', 'architecture', 'platformVersion', 'fullVersionList']);

      const browserInfo =
        uaData.fullVersionList?.[uaData.fullVersionList.length - 1] ||
        (navigator.userAgentData as { brands: { brand: string; version: string }[] }).brands?.[0];
      const browserString = browserInfo ? `${browserInfo.brand} ${browserInfo.version}` : 'Unknown Browser';

      return `${browserString} on ${uaData.platform} ${uaData.platformVersion} (${uaData.architecture})`;
    } catch {
      const browserInfo = (navigator.userAgentData as { brands: { brand: string; version: string }[] }).brands?.[0];
      const browserString = browserInfo ? `${browserInfo.brand} ${browserInfo.version}` : 'Unknown Browser';

      return `${browserString} on ${(navigator.userAgentData as { platform: string }).platform} ${navigator.userAgent}`;
    }
  }

  private handleBugReport(bugReport: IBugReport): void {
    this.createBugReport(bugReport);
  }

  private createBugReport(bugReport: IBugReport): void {
    this.http.post('user-feedback', bugReport).subscribe({
      next: () => {
        this.snackbar.openSuccess(this.translate.instant(`shared.bugReport.${camelCase(bugReport.type)}Success`));
      },
    });
  }
}
